// ===========================================
// Lesson Info Card Component
// ===========================================

import {
  Paper,
  Typography,
  Grid,
  Chip,
  Divider,
  Box,
  Alert,
  Tooltip,
  Button,
} from '@mui/material';
import { EventRepeat as EventRepeatIcon, WarningAmber as WarningAmberIcon } from '@mui/icons-material';
import { Lesson, getLessonTypeColor, LessonExceptionData } from '../../../api/lessons.api';
import { RelatedTerm } from './lessonDetail.types';
import { useReplacementCoverage } from '../../../hooks/useLessons';
import { usePermissions } from '../../../hooks/usePermissions';
import { currentWeekNumber, formatUTCDate, parseUTCDate, termWeekCount, formatTermWeekDate, weekInLessonWindow } from '../../../utils/dateUtils';
import { ownClassWeeks } from '../lessons/lessons.helpers';

interface LessonInfoCardProps {
  lesson: Lesson;
  relatedTerms: RelatedTerm[];
  exceptions?: LessonExceptionData[];
  /**
   * When provided (admin with scheduling.write), renders the "Open weeks"
   * affordance beside the Individual Weeks chips — opens the management
   * dialog for HybridLessonPattern.openClinicWeeks.
   */
  onManageOpenClinicWeeks?: () => void;
  /**
   * When provided, sibling term chips navigate to that term's lesson row.
   * A multi-term class is separate Lesson rows (one per term) — this page
   * shows ONE of them, so the other terms' chips act as links.
   */
  onNavigateToTermLesson?: (lessonId: string) => void;
  /**
   * When provided (admin with scheduling.write), each cancelled occurrence gets a
   * "Restore" button — the ONLY un-cancel UI in the app (the calendar's context
   * menu has none). Restoring deletes the CANCELLED exception, so the occurrence
   * is live again and its Google event is retitled back.
   */
  onRestoreCancelled?: (exceptionId: string) => void;
  /** The exception currently being restored (disables its button). */
  restoringExceptionId?: string | null;
}

export default function LessonInfoCard({
  lesson,
  onRestoreCancelled,
  restoringExceptionId,
  relatedTerms,
  exceptions,
  onManageOpenClinicWeeks,
  onNavigateToTermLesson,
}: LessonInfoCardProps) {
  // Per-week replacements live on LessonException, not the lesson row — pull the
  // coverage (cached by the page's own query) so this card shows them. The
  // legacy whole-term field (lesson.replacementTeacherId) is still honoured.
  // The route needs scheduling.read; without it the card shows the legacy field only (#272;
  // a teacher-scoped read is #526).
  const { can } = usePermissions();
  const { data: coverage } = useReplacementCoverage(lesson.id, { enabled: can('scheduling', 'read') });
  // A window SHRINK keeps its LessonException rows, so cancelled / covered rows can name
  // weeks the class no longer runs. Every reader ignores them (a "Restore" button for a
  // week that does not exist is a trap), so filter by the class window.
  const runsInWeek = (week: number): boolean =>
    !lesson.Term?.startDate || !lesson.Term?.endDate
      ? true
      : weekInLessonWindow(week, lesson, termWeekCount(lesson.Term.startDate, lesson.Term.endDate));
  // Group per-week coverage by teacher → "Jane Smith · Wks 2, 3".
  const perWeekChips = (() => {
    const coveredWeeks = (coverage?.weeks ?? []).filter((w) => runsInWeek(w.weekNumber));
    if (!coverage || coveredWeeks.length === 0) return [] as { name: string; color: string | null; weeks: number[] }[];
    const byTeacher = new Map<string, { name: string; color: string | null; weeks: number[] }>();
    for (const w of coveredWeeks) {
      const t = w.replacementTeacher;
      const entry = byTeacher.get(t.id) ?? { name: `${t.firstName} ${t.lastName}`, color: t.color, weeks: [] };
      entry.weeks.push(w.weekNumber);
      byTeacher.set(t.id, entry);
    }
    return Array.from(byTeacher.values()).map((e) => ({ ...e, weeks: e.weeks.sort((a, b) => a - b) }));
  })();
  const hasPerWeek = perWeekChips.length > 0;
  const hasGlobal = !!lesson.replacementTeacherId && !!lesson.ReplacementTeacher;
  // Check if there are any cancelled occurrences
  const cancelledExceptions =
    exceptions?.filter((exc) => {
      if (exc.type !== 'CANCELLED') return false;
      if (!lesson.Term?.startDate || !exc.originalDate) return true;
      return runsInWeek(currentWeekNumber(lesson.Term.startDate, parseUTCDate(exc.originalDate)));
    }) || [];

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Class Details
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {/* Show cancellation alert if there are cancelled occurrences */}
      {cancelledExceptions.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Class Cancelled
          </Typography>
          {[...cancelledExceptions]
            .sort((a, b) => String(a.originalDate).localeCompare(String(b.originalDate)))
            .map((exc) => (
            <Box key={exc.id} sx={{ fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption">
                {/* "Week N dd/mm/yyyy" — the term week the admin picked, then the
                    recurring occurrence date (UTC getters: originalDate is a @db.Date). */}
                {lesson.Term?.startDate && exc.originalDate
                  ? `Week ${currentWeekNumber(lesson.Term.startDate, parseUTCDate(exc.originalDate))} `
                  : ''}
                {exc.originalDate && formatUTCDate(exc.originalDate, {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                }, 'en-AU')}
              </Typography>
              {onRestoreCancelled && (
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  onClick={() => onRestoreCancelled(exc.id)}
                  disabled={restoringExceptionId === exc.id}
                  sx={{ py: 0, minHeight: 24, fontSize: '0.75rem' }}
                >
                  {restoringExceptionId === exc.id ? 'Restoring…' : 'Restore'}
                </Button>
              )}
              {exc.reason && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, flexBasis: '100%' }}>
                  Reason: {exc.reason}
                </Typography>
              )}
            </Box>
          ))}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Row 1: Instrument | Teacher */}
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Instrument
          </Typography>
          <Box>
            {lesson.Instrument ? (
              <Chip
                label={lesson.Instrument.name}
                size="small"
                sx={lesson.Instrument.color ? {
                  backgroundColor: lesson.Instrument.color,
                  color: 'white',
                } : undefined}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">Not specified</Typography>
            )}
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Teacher
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {/* Primary teacher. Struck through only when a legacy whole-term
                replacement covers every week; with per-week coverage the
                primary still teaches the un-replaced weeks, so keep it normal. */}
            <Chip
              label={`${lesson.Instructor?.User.firstName} ${lesson.Instructor?.User.lastName}`}
              size="small"
              sx={{
                backgroundColor: lesson.Instructor?.color,
                color: lesson.Instructor?.color ? 'white' : undefined,
                ...(hasGlobal ? { textDecoration: 'line-through', opacity: 0.6 } : {}),
              }}
              title={hasGlobal ? 'Original teacher (replaced)' : 'Teacher'}
            />

            {/* Legacy whole-term replacement */}
            {hasGlobal && (
              <Chip
                label={`${lesson.ReplacementTeacher?.User.firstName} ${lesson.ReplacementTeacher?.User.lastName}`}
                size="small"
                color="warning"
                sx={lesson.ReplacementTeacher?.color ? {
                  backgroundColor: lesson.ReplacementTeacher.color,
                  color: 'white',
                } : undefined}
                title="Replacement teacher (all remaining weeks)"
              />
            )}

            {/* Per-week replacements — one chip per teacher with covered weeks */}
            {hasPerWeek && perWeekChips.map((c) => (
              <Chip
                key={c.name}
                label={`${c.name} · Wk${c.weeks.length > 1 ? 's' : ''} ${c.weeks.join(', ')}`}
                size="small"
                color="warning"
                sx={c.color ? { backgroundColor: c.color, color: 'white' } : undefined}
                title="Replacement teacher (selected weeks)"
              />
            ))}
          </Box>
        </Grid>

        {/* Row 2: Type | Status */}
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Type
          </Typography>
          <Box>
            <Chip
              label={lesson.LessonType.name}
              size="small"
              color={lesson.LessonType.color ? undefined : getLessonTypeColor(lesson.LessonType.type)}
              sx={lesson.LessonType.color ? {
                backgroundColor: lesson.LessonType.color,
                color: 'white',
              } : undefined}
            />
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Status
          </Typography>
          <Box>
            <Chip
              label={lesson.isActive ? 'Active' : 'Inactive'}
              size="small"
              color={lesson.isActive ? 'success' : 'default'}
            />
          </Box>
        </Grid>

        {/* Row 3: Program Level (+ Stream chip — the stream is a sub-classification of the level) */}
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Program Level
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {lesson.ProgramLevel ? (
              <Chip
                label={lesson.ProgramLevel.name}
                size="small"
                sx={lesson.ProgramLevel.color ? {
                  backgroundColor: lesson.ProgramLevel.color,
                  color: 'white',
                } : undefined}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">Not specified</Typography>
            )}
            {lesson.Stream && (
              <Chip
                label={lesson.Stream.name}
                size="small"
                sx={lesson.Stream.color ? {
                  backgroundColor: lesson.Stream.color,
                  color: 'white',
                } : undefined}
                title="Stream"
              />
            )}
          </Box>
        </Grid>

        {/* Row 4: Terms (full width) */}
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">
            {relatedTerms.length > 1 ? 'Terms' : 'Term'}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {relatedTerms.length > 0 ? (
              // Each chip is a separate Lesson ROW (multi-term class = one row
              // per term). The filled chip is the row THIS page shows — week
              // config edits (e.g. open clinic weeks) apply to it only. Other
              // terms' chips navigate to that term's row.
              relatedTerms.map((term) => {
                const isThisRow = term.id === lesson.id;
                const clickable = !isThisRow && !!onNavigateToTermLesson;
                return (
                  <Chip
                    key={term.id}
                    label={term.termName}
                    size="small"
                    color={isThisRow ? 'primary' : 'default'}
                    variant={isThisRow ? 'filled' : 'outlined'}
                    onClick={clickable ? () => onNavigateToTermLesson(term.id) : undefined}
                  />
                );
              })
            ) : (
              <Typography>{lesson.Term.name}</Typography>
            )}
          </Box>
        </Grid>

        {/* Mid-term window: only when the class does not run the full term */}
        {(() => {
          const startWeek = lesson.startWeek ?? null;
          const endWeek = lesson.endWeek ?? null;
          if (startWeek === null && endWeek === null) return null;
          // Both ends (or only an end) read as a range; a start alone reads as a dated start.
          const label =
            endWeek !== null && (startWeek ?? 1) === endWeek
              ? lesson.Term?.startDate
                ? `${formatTermWeekDate(lesson.Term.startDate, endWeek, lesson.dayOfWeek)} only`
                : `Week ${endWeek} only`
              : endWeek !== null
              ? `Weeks ${startWeek ?? 1}–${endWeek}`
              : lesson.Term?.startDate
                ? `Starts ${formatTermWeekDate(lesson.Term.startDate, startWeek as number, lesson.dayOfWeek)}`
                : `Starts Week ${startWeek}`;
          return (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Runs
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip label={label} size="small" variant="outlined" color="info" data-testid="class-window-chip" />
              </Box>
            </Grid>
          );
        })()}

        {/* Hybrid Week Schedule — a HYBRID class only. A pattern left on a class whose type
            was changed away from Hybrid is not this class's schedule (MNM, 07/10/2026). */}
        {lesson.LessonType?.type === 'HYBRID' && lesson.HybridLessonPattern && (() => {
          const { hybridCombination, groupWeeks, individualWeeks, bandWeeks } = lesson.HybridLessonPattern;
          const combination = hybridCombination ?? 'GROUP_INDIVIDUAL';

          // Phase E (#168): a stored week the term does not have used to render as an
          // ordinary chip, identical to the valid ones — which is how 46 classes carried
          // an unscheduled, billed week 11 unnoticed. Null-tolerant: a row without term
          // dates (a fixture, a partial load) renders as before.
          const lastWeek =
            lesson.Term?.startDate && lesson.Term?.endDate
              ? termWeekCount(lesson.Term.startDate, lesson.Term.endDate)
              : null;
          // `color` doubles as the bucket: 'warning' is the band bucket, whose weeks are a
          // read-only snapshot of the BAND class's rehearsal weeks — the fix lives there,
          // not on this class (`/qa` E M1; a CUSTOM band week has no term bound, #173).
          const weekChip = (week: number, color: 'success' | 'primary' | 'warning') => {
            if (lastWeek === null || week <= lastWeek) {
              return <Chip key={week} label={`Week ${week}`} size="small" color={color} />;
            }
            const remedy =
              color === 'warning'
                ? `Its band's rehearsal weeks are set on the band class for ${lesson.Term.name} — edit those, then save this class again.`
                : 'Edit the class to remove it.';
            const title = `Week ${week} doesn't exist in ${lesson.Term.name} (${lastWeek} weeks) — it is billed but never scheduled. ${remedy}`;
            return (
              <Tooltip key={week} title={title}>
                <Chip
                  label={`Week ${week}`}
                  size="small"
                  color="error"
                  variant="outlined"
                  icon={<WarningAmberIcon />}
                  tabIndex={0}
                  aria-label={title}
                  data-testid="out-of-term-week"
                />
              </Tooltip>
            );
          };

          const showGroup = combination === 'GROUP_INDIVIDUAL' || combination === 'GROUP_BAND';
          const showIndividual = combination === 'GROUP_INDIVIDUAL' || combination === 'INDIVIDUAL_BAND';
          const showBand = combination === 'GROUP_BAND' || combination === 'INDIVIDUAL_BAND';

          return (
            <>
              {showGroup && groupWeeks.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Group Weeks
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {groupWeeks.map((week) => weekChip(week, 'success'))}
                  </Box>
                </Grid>
              )}
              {showIndividual && individualWeeks.length > 0 && (() => {
                // Clinic weeks the admin has opened to any term week — those
                // chips announce it; the "Open weeks" chip manages the state.
                const openWeeks = (lesson.HybridLessonPattern?.openClinicWeeks ?? []) as number[];
                return (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Individual Weeks
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      {individualWeeks.map((week) => {
                        const isOpen = openWeeks.includes(week);
                        return isOpen ? (
                          <Tooltip
                            key={week}
                            title="Open — families can book this clinic in any week the teacher is available"
                          >
                            <Chip
                              label={`Week ${week} · Open`}
                              size="small"
                              icon={<EventRepeatIcon />}
                              sx={{ bgcolor: '#a3d9f6', color: '#1565c0', fontWeight: 600 }}
                            />
                          </Tooltip>
                        ) : (
                          weekChip(week, 'primary')
                        );
                      })}
                      {onManageOpenClinicWeeks && (
                        <Tooltip title="Open a clinic week to any week (e.g. the teacher is away)">
                          <Chip
                            label="Open weeks…"
                            size="small"
                            variant="outlined"
                            icon={<EventRepeatIcon />}
                            onClick={onManageOpenClinicWeeks}
                            sx={{ ml: 0.5 }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </Grid>
                );
              })()}
              {showBand && bandWeeks && bandWeeks.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Band Weeks
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {bandWeeks.map((week) => weekChip(week, 'warning'))}
                  </Box>
                </Grid>
              )}
            </>
          );
        })()}

        {/* Class weeks — a band's rehearsal weeks (#173 Phase 3) and an INDIVIDUAL / GROUP
            class's chosen weeks (fortnightly etc., class weeks Phase 7): the class's OWN stored
            weeks. Unlike the hybrid `bandWeeks` bucket above (a read-only snapshot whose fix
            lives on the band class), these ARE this class's data, so the remedy names this
            class. Scoped to the lesson's own term: the row is per term, and a row for another
            term must never be read as this one's. */}
        {(() => {
          // ⚠️ Gated on the lesson TYPE, not merely on a row existing: a HYBRID's pattern
          // governs it (a stale row would be confusing and would hide the real defect).
          const type = lesson.LessonType?.type;
          if (type !== 'BAND' && type !== 'INDIVIDUAL' && type !== 'GROUP') return null;
          const isBand = type === 'BAND';
          const own = ownClassWeeks(lesson);
          const ownBandTerm = own && own.termId === lesson.termId ? own : null;
          const rehearsalWeeks = ownBandTerm?.weeks ?? [];
          if (!ownBandTerm || rehearsalWeeks.length === 0) return null;
          // An INDIVIDUAL / GROUP class with no chosen list runs every week: nothing to show
          // (D2: "Every week" stores no row; a leftover EVERY_WEEK row reads the same).
          if (!isBand && ownBandTerm.patternType !== 'CUSTOM') return null;

          // Null-tolerant, as the hybrid block is: a row without term dates (a fixture,
          // a partial load) renders plain chips rather than guessing a bound.
          const lastWeek =
            lesson.Term?.startDate && lesson.Term?.endDate
              ? termWeekCount(lesson.Term.startDate, lesson.Term.endDate)
              : null;

          return (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" data-testid="class-weeks-caption">
                {isBand
                  ? `Rehearsal Weeks${ownBandTerm.patternType === 'EVERY_WEEK' ? ' (auto-calculated)' : ''}`
                  : `Lesson weeks ${[...rehearsalWeeks]
                      .filter((w) => lastWeek === null || w <= lastWeek)
                      .sort((a, b) => a - b)
                      .join(', ')}`}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {rehearsalWeeks.map((week) => {
                  if (lastWeek === null || week <= lastWeek) {
                    return <Chip key={week} label={`Week ${week}`} size="small" color="info" />;
                  }
                  // ⚠️ The remedy must match what the admin can actually DO. A CUSTOM
                  // band's weeks are editable in the class dialog. An EVERY_WEEK band's
                  // are DERIVED (`calculateBandWeeks`) and render read-only there, so
                  // "edit the rehearsal weeks" would be a dead end. That state is
                  // reachable: the weeks are bounded at derive time, but `updateTerm`
                  // never re-derives them, so shortening a term strands a week beyond
                  // the new bound. (Whole-feature /qa, 2026-09-23.)
                  const remedy = !isBand
                    ? 'Edit this class’s weeks to remove it.'
                    : ownBandTerm.patternType === 'EVERY_WEEK'
                      ? `They are auto-calculated from ${lesson.Term.name}'s dates — re-save this class to recalculate them.`
                      : 'Edit this band’s rehearsal weeks to remove it.';
                  // An INDIVIDUAL / GROUP class bills and schedules only the weeks inside its
                  // term (`classSessionWeeks` clips to the window), so the week does nothing.
                  const title = isBand
                    ? `Week ${week} doesn't exist in ${lesson.Term.name} (${lastWeek} weeks) — ` +
                      `it is billed but never rehearsed. ${remedy}`
                    : `Week ${week} doesn't exist in ${lesson.Term.name} (${lastWeek} weeks), so it ` +
                      `has no lesson and is not billed. ${remedy}`;
                  return (
                    <Tooltip key={week} title={title}>
                      <Chip
                        label={`Week ${week}`}
                        size="small"
                        color="error"
                        variant="outlined"
                        icon={<WarningAmberIcon />}
                        tabIndex={0}
                        aria-label={title}
                        data-testid="out-of-term-week"
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            </Grid>
          );
        })()}

        {/* Replacement Reason (if replacement exists) */}
        {lesson.replacementReason && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Replacement Reason
            </Typography>
            <Typography sx={{ color: 'warning.main', fontStyle: 'italic' }}>
              {lesson.replacementReason}
            </Typography>
          </Grid>
        )}

        {/* Description (if exists) */}
        {lesson.description && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Description
            </Typography>
            <Typography>{lesson.description}</Typography>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
}
